import React from 'react';
import {
  useApprovalPathRequestsQuery,
  ApprovalRequestStatus,
  Colleague,
} from '../../../__generated__/graphql';
import ServerError from '../../../../../components/ServerError';
import { Loading } from '../ApprovalRequestsTable/ApprovalRequestDetailsSidebar/AbsenceDetails';
import { mapApprovalPath } from './mappings';
import { useUserInfo } from '../../../context/UserInfo';
import { EmployeeWithAvatar } from '../../../components/Employee';
import './ApprovalPath.css';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { TimeSince } from '../../../../../components/TimeSince';
import StatusIcon from '../../../../../atomic/atoms/StatusIcon/StatusIcon';

export type ApprovalRequestProps = {
  Id: string;
  approver: Pick<Colleague, 'Id' | 'Name' | 'department' | 'avatar'>;
  recordEmployee: Pick<Colleague, 'Id' | 'Name' | 'department' | 'avatar'>;
  flair__Approval_Status__c: ApprovalRequestStatus;
  CreatedDate: string;
};

type ApprovalPathProps = {
  relatedRecordId: string;
};

const i18Path = 'approvalRequest';

export const ApprovalPath: React.FC<
  React.PropsWithChildren<ApprovalPathProps>
> = ({ relatedRecordId }) => {
  const t = useNamespacedTranslation(i18Path);
  const { data, loading, error } = useApprovalPathRequestsQuery({
    variables: { relatedRecordId },
  });

  const currentEmployeeId = useUserInfo().id;

  if (loading) return <Loading />;
  if (error) return <ServerError />;

  const filteredApprovalRequests = data?.approvalRequests.filter(
    (request) => request.approver,
  ) as ApprovalRequestProps[];

  const approvalPath = mapApprovalPath(
    filteredApprovalRequests,
    currentEmployeeId,
    t,
  );

  if (!approvalPath || approvalPath.length === 0) {
    return <div>{t('noApprovalRequest')}</div>;
  }

  return (
    <div>
      <h3 className="mb-5">{t('approvalPath.whatToExpectNext')}</h3>
      <div className="d-flex flex-column gap-3 ps-3">
        {approvalPath.map((item) => (
          <div key={item.id} className="d-flex mb-4 approval-path-item">
            <div className="text-muted d-flex align-items-center gap-2 status-container">
              {item.status && <StatusIcon status={item.status} />}
              <div className="d-flex flex-column gap-1 small ms-2">
                <span className="fw-bold">{item.additionalInfo1}</span>
                <span className="text-muted">
                  {item.isApprover ? (
                    item.statusLabel
                  ) : (
                    <TimeSince date={new Date(item.createdDate)} />
                  )}
                </span>
              </div>
            </div>
            <EmployeeWithAvatar
              employee={{
                name: item.name,
                avatarUrl: item.avatarUrl,
              }}
              additionalInfo={item.departmentName}
              size="sm"
              nameContainerClassName="fw-bold"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ApprovalPath;
